<template>
    <div class="free-sim-plan-complete">
        <processing v-if="!ready" :full-screen="true" />
        <div v-else class="main-column">
            <header class="hero">
                <h1 class="hero__title">{{ titleText }}</h1>
                <p class="hero__lead">
                    {{ $t('order_complete.timeNoteDataPlan') }}
                    {{ $t('order_complete.restart_device_tip') }}
                </p>
            </header>

            <div class="lower-block">
                <section class="plan-shell" aria-labelledby="sim-free-plan-heading">
                    <h2 id="sim-free-plan-heading" class="plan-shell__heading">
                        {{ $t('update_plan.sub_title') }}
                    </h2>
                    <div class="plan-shell__panel">
                        <div class="plan-row">
                            <span class="plan-row__label">{{ planNameLabel }}:</span>
                            <span class="plan-row__value plan-row__value--price">{{ displayPrice }}</span>
                        </div>
                        <div class="plan-row">
                            <span class="plan-row__label">{{ $t('switch_plan.valid_time') }}:</span>
                            <span class="plan-row__value">{{ validTimePrimary }}</span>
                        </div>
                    </div>
                </section>

                <div class="cta-wrap">
                    <reo-common-button
                        v-if="config.platform === 'web'"
                        :to="{ path: '/user/dashboard/', query: { tab: 'sim' } }"
                        :label="$t('cart.finish')"
                        :customWidth="finishCustomWidth"
                    />
                    <reo-common-button
                        v-else-if="config.platform === 'app'"
                        type="button"
                        @click="onAppFinish"
                        :label="$t('cart.finish')"
                        :customWidth="finishCustomWidth"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapGetters } from 'vuex';
import processing from '@/basic/processing.vue';
import { timeResolve } from '@/common/js/date';
import { config } from 'CONFIG';
import { unfoldError } from '@/common/js/notice';
import { deliver as deliverStatus, currStage as currentStage } from '@/config/status';
import nprogress from 'nprogress';
import { getOrderDetailById } from '@/api/cart';
import { AxiosError } from 'axios';
import { productsManager } from '@/lib';
import { ESubscribeMode, IOrder } from '@reolink-cloud/interface';
import { getSubscriptionById } from '@/api/subscriptions';
import { callAppApi } from '@reolink-web/app-sdk';
import { isApp } from '@/utils/business';
import { Async } from '@reolink-web/utils';

/** SIM 免费领取完成页 — 版式对齐 MasterGo 135:18302 主列（DSL：1000 列宽、60 纵向间距、卡片双阶与主按钮）。 */
export default Vue.extend({

    name: 'FreeSimPlanComplete',

    components: { processing },

    data () {

        return {
            config,
            ready: false,
            mode: this.$route.query.mode as string,
            orderId: this.$route.query.id as string,
            timer: null as null | number,
            planTitle: '',
            planUnit: '',
            planFrequency: 0,
            salePrice: '',
            startedAt: 0,
            endingAt: 0,
            planId: '',
            subscriptionId: '',
        };

    },

    computed: {
        ...mapGetters( ['isMobile'] as const ),
        lang (): string {

            return this.$store.getters.lang;

        },
        titleText (): string {

            return ( this.mode === ESubscribeMode.RENEW ?
                this.$t( 'switch_plan.renewal_free' ) :
                this.$t( 'cart.claim_success' )) as string;

        },
        planNameLabel (): string {

            const cycle = this.$tc( `cycle.${this.planUnit}`, this.planFrequency, {

                count: this.planFrequency

            });

            return `${this.planTitle} (${cycle})`;

        },
        isFree (): boolean {

            return this.salePrice === '0.00' || Number( this.salePrice ) === 0;

        },
        displayPrice (): string {

            return this.isFree ? ( this.$t( 'plan.free' ) as string ) : this.salePrice;

        },
        validTimePrimary (): string {

            return `${this.formatInstant( this.startedAt )} – ${this.formatInstant( this.endingAt )}`;

        },
        validTimeExtra (): string {

            return this.isFree ? ( this.$t( 'switch_plan.free_tip' ) as string ) : '';

        },
        finishCustomWidth(): number|string {

            return this.isMobile ? '100%' : 200;

        }
    },

    async created () {

        try {

            await productsManager.update({ lang: this.lang });

            let order = {} as IOrder;
            await new Promise<void>(( resolve, reject ) => {

                // eslint-disable-next-line @typescript-eslint/no-misused-promises -- polling handler
                this.timer = window.setInterval( async (): Promise<void> => {

                    try {

                        order = await getOrderDetailById( this.orderId );

                        if ( !( order.status.currStage === currentStage.DELIVER && order.status.deliverStatus === deliverStatus.PENDING )) {

                            window.clearInterval( this.timer! );
                            resolve();

                        }

                    } catch ( err ) {

                        reject( err );

                    }

                }, 1000 );

            });

            const serviceId = order.items[0].context.trackingInfo?.[0].shippedProduct.serviceId;
            if ( !serviceId ) {

                unfoldError( 'errors.server_error', 100000 );
                return;

            }

            const sub = await getSubscriptionById( serviceId );
            const planMeta = await productsManager.getPlanById( order.items[0].productId );

            this.planId = order.items[0].productId;
            this.subscriptionId = order.items[0].context.subscription ?? '';
            this.planTitle = sub.title ?? planMeta.series?.i18n?.name ?? '';
            this.startedAt = sub.startedAt;
            this.endingAt = sub.endingAt;
            this.planUnit = planMeta.duration.unit;
            this.planFrequency = planMeta.duration.amount;
            this.salePrice = planMeta.pricing.salePrice;

            this.ready = true;

            if ( isApp ) {

                Async.invokeAsync(() => callAppApi( 'simCardActivated' ));

            }

            nprogress.done();

        } catch ( err ) {

            unfoldError( 'errors.server_error', 10000 );
            console.error( err );
            const symbol = ( err as AxiosError ).response?.data?.error?.symbol;
            if ( symbol === 'order_not_found' ) {

                await this.$router.push( '/user/cloud-plan-payment-history/' );

            }

        }

    },

    beforeDestroy () {

        if ( this.timer != null ) {

            window.clearInterval( this.timer );

        }

    },

    methods: {

        formatInstant ( ts: number ): string {

            const { year, month, dateOfMonth, hour } = timeResolve( ts );

            const datePart = this.$t( 'date_format.default', {
                year,
                month,
                day: dateOfMonth
            }) as string;

            return `${datePart} ${hour}:00`;

        },

        async onAppFinish (): Promise<void> {

            await callAppApi( 'finishPage', { planId: this.planId, subscriptionId: this.subscriptionId });

        },
    },

});
</script>

<style lang="less" scoped>
/* 主列：DSL flexContainer gap 60px, padding 60px 0 80px, width 1000 */
.free-sim-plan-complete {
    background-color: @bg_01;
    color: @font_primary;
}

.main-column {
    box-sizing: border-box;
    width: 100%;
    max-width: 1000px;
    box-sizing: border-box;
    margin: 0 auto;
    padding: 60px 0px 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 60px;
    @media @snapeak_mobile {
        padding: 48px 20px 64px;
        gap: 40px;
    }
}

/* 135:18303：标题 + 副文案，列 gap 4px，副文案 #777 16 Medium */
.hero {
    width: 100%;
    max-width: 710px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 4px;
}

.hero__title {
    margin: 0;
    font-family: @font_01;
    font-size: 32px;
    font-weight: 600;
    color: @font_primary;
    .mobile({
        font-size: 30px
    });
}

.hero__lead {
    margin: 0;
    font-family: @font_01;
    font-size: 16px;
    font-weight: 500;
    color: @font_secondary;
    .mobile({
        font-size: 14px;
    });
}

/* 方案卡 + 联系文案 + 按钮：DSL 内层区块相对间距 24px */
.lower-block {
    width: 100%;
    max-width: 1000px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
}

/* Step1 外层：bg_02、圆角 12、内边距 32、内部纵向 gap 16 */
.plan-shell {
    width: 100%;
    background-color: @bg_02;
    border-radius: 12px;
    padding: 32px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: left;
    gap: 16px;
    @media @snapeak_mobile {
        padding: 16px;
    }
}

.plan-shell__heading {
    margin: 0;
    font-family: @font_01;
    font-size: 20px;
    font-weight: 600;
    color: @font_primary;
    text-align: left;
    .mobile({
        font-size: 16px;
    });
}

/* 内层面板：bg_01、圆角 8、padding 16 24、行 gap 12 */
.plan-shell__panel {
    width: 100%;
    box-sizing: border-box;
    background-color: @bg_01;
    border-radius: 8px;
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    .mobile({
        padding: 12px;
    });
}

.plan-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 12px;
    font-family: @font_01;
    font-size: 16px;
    .mobile({
        flex-direction: column;
        font-size: 14px;
        line-height: 16px;
        gap: 4px;
    });
}

.plan-row__label {
    color: @font_secondary;
    font-weight: 400;
}

.plan-row__value {
    color: @font_primary;
    font-weight: 500;
}

.plan-row__value--price {
    color: #FF8836;
    font-weight: 500;
}

.plan-row__extra {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    color: @font_secondary;
}

.contact-block {
    margin: 0;
    width: 100%;
    max-width: 710px;
    text-align: center;
    font-family: @font_01;
    font-size: 16px;
    font-weight: 500;
    color: @font_secondary;
}

.cta-wrap {
    display: flex;
    justify-content: center;
    width: 100%;
}

/* Primary：#0050E2、圆角 8、阴影（DSL effect_4:500） */
.primary-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 200px;
    min-height: 46px;
    padding: 12px 24px;
    box-sizing: border-box;
    border: none;
    border-radius: 8px;
    background-color: #0050e2;
    color: #fff;
    font-size: 17px;
    font-weight: 600;
    line-height: 22px;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.primary-cta:hover {
    background-color: #0046c7;
    box-shadow: 0 4px 16px rgba(0, 157, 225, 0.45);
    color: #fff;
    text-decoration: none;
}
</style>
